import type { SheetData } from 'write-excel-file/universal';
import { SeasonStats } from '../models/seasonStats';
import { Stats } from '../models/stats';

export const SEASON_EXPORT_COLUMNS: Array<{ field: keyof Stats; key: string }> = [
  { field: 'playerName', key: 'player' },
  { field: 'playedGames', key: 'playedGames' },
  { field: 'playerWins', key: 'wins' },
  { field: 'playerDraws', key: 'draws' },
  { field: 'playerLoses', key: 'losses' },
  { field: 'playerScored', key: 'teamGoalsFor' },
  { field: 'playerConceded', key: 'teamGoalsAgainst' },
  { field: 'playerScore', key: 'playerScore' },
  { field: 'teamBoostScore', key: 'teamBoost' },
  { field: 'teamBoostGames', key: 'teamBoostGames' },
  { field: 'playedAsWhites', key: 'whiteGames' },
  { field: 'playedAsBlacks', key: 'blackGames' },
  { field: 'didNotRespond', key: 'noResponse' },
  { field: 'wasNotInvited', key: 'notInvited' },
  { field: 'declinedInvite', key: 'declined' }
];

// Season boundaries are calendar dates stored at UTC midnight; end is exclusive.
export function seasonExportDates(season: SeasonStats): { start: string; end: string } {
  const start = new Date(season.seasonStart);
  const end = new Date(season.seasonEnd);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    throw new Error('Invalid season boundaries');
  }
  end.setUTCDate(end.getUTCDate() - 1);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

export function seasonExportData(season: SeasonStats, translate: (key: string) => string): SheetData {
  const rows = (season.stats || []).filter(Boolean).slice().sort((a, b) =>
    (b.playerScore || 0) - (a.playerScore || 0) || (a.playerName || '').localeCompare(b.playerName || ''));
  return [
    SEASON_EXPORT_COLUMNS.map(column => ({ type: String, value: translate('stats.export.columns.' + column.key), fontWeight: 'bold' as const })),
    ...rows.map(row => SEASON_EXPORT_COLUMNS.map(column => {
      const value = row[column.field];
      // Names must always remain literal text, even if they start with '=' or '+'.
      if (column.field === 'playerName') return { type: String, value: String(value || '') };
      return { type: Number, value: typeof value === 'number' && Number.isFinite(value) ? value : 0,
        format: ['playerScore', 'teamBoostScore'].includes(column.field) ? '0.00' : '0' };
    }))
  ];
}

export async function downloadSeasonStats(season: SeasonStats, matchName: string, translate: (key: string) => string): Promise<void> {
  const dates = seasonExportDates(season);
  const { default: writeExcelFile } = await import('write-excel-file/universal');
  const blob = await writeExcelFile([
    { sheet: translate('stats.export.playersSheet'), data: seasonExportData(season, translate),
      columns: SEASON_EXPORT_COLUMNS.map((_, index) => ({ width: index === 0 ? 30 : 23 })), stickyRowsCount: 1, stickyColumnsCount: 1 },
    { sheet: translate('stats.export.summarySheet'), data: [
      [{ type: String, value: translate('stats.export.match') }, { type: String, value: matchName }],
      [{ type: String, value: translate('stats.export.start') }, { type: String, value: dates.start }],
      [{ type: String, value: translate('stats.export.end') }, { type: String, value: dates.end }],
      [{ type: String, value: translate('stats.export.created') }, { type: String, value: new Date().toISOString() }],
      [{ type: String, value: translate('stats.export.notes') }, { type: String, value: translate('stats.export.notesText') }]
    ], columns: [{ width: 26 }, { width: 95 }] }
  ]).toBlob();
  const name = (matchName || 'Footballer').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-').slice(0, 90);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}_${dates.start}_${dates.end}.xlsx`;
  document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000); }
}
